import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Order, LogElement } from '@models/order';
import { OrderService } from '@services/order.service';
import { FormatData } from '@classes/format-data';

export class NewRxJob {
  static rxInvoicePrefix(invoiceNumber: string): string {
    return FormatData.displayInvoiceNumber(invoiceNumber).replace(
      /-RX\d*$/i,
      ''
    );
  }

  static nextRxInvoice(originalInvoice: string, orders: Order[]): string {
    const prefix = NewRxJob.rxInvoicePrefix(originalInvoice);
    if (!prefix) {
      return '';
    }
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-RX(\\d+)?$`, 'i');
    let max = 0;
    (orders || []).forEach((order) => {
      const invoice = FormatData.displayInvoiceNumber(order?.invoiceNumber);
      const match = invoice.match(pattern);
      if (!match) {
        return;
      }
      const n = match[1] ? parseInt(match[1], 10) : 1;
      if (!isNaN(n) && n > max) {
        max = n;
      }
    });
    if (max === 0) {
      return `${prefix}-RX`;
    }
    return `${prefix}-RX${max + 1}`;
  }

  static parseOrderTax(order: any): number {
    const raw = order?.products?.[0]?.tax ?? order?.tax ?? 0;
    if (typeof raw === 'number') {
      return isNaN(raw) ? 0 : raw;
    }
    const parsed = parseFloat(String(raw).replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  static shouldApplyFrameOnlyTaxCredit(original: any, orders: Order[]): boolean {
    if (!original?.frameOnly || original?.frameOnlyTaxCreditUsed) {
      return false;
    }
    const invoice = String(original?.invoiceNumber || '').trim();
    if (!invoice) {
      return false;
    }
    const alreadyUsed = (orders || []).some((order) => {
      const linked = String((order as any)?.linkedInvoice || '').trim();
      const credit = Number((order as any)?.frameOnlyTaxCredit) || 0;
      return linked === invoice && credit > 0;
    });
    return !alreadyUsed;
  }

  static formatTaxCredit(amount: number): string {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  static buildLinkedNewRxOrder(
    original: any,
    originalInvoice: string,
    invoiceNumber: string,
    taxCredit: number,
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    }
  ): Order {
    const clone = JSON.parse(JSON.stringify(original || {}));
    const credit = taxCredit > 0 ? taxCredit : 0;
    clone._id = '';
    clone.invoiceNumber = invoiceNumber;
    clone.linkedInvoice = originalInvoice;
    clone.frameOnly = false;
    clone.frameOnlyTaxCredit = credit;
    clone.frameOnlyTaxCreditUsed = false;
    clone.exchangeCredit = 0;
    clone.status = '';
    clone.todo = 'SAVE';
    clone.jobAction = '';
    clone.jobActions = [];
    clone.lensStatus = '';
    clone.rxStatus = '';
    clone.eta = '';
    clone.archived = false;
    clone.framesRemoved = [];
    clone.tax = 0;
    clone.shipping = 0;
    clone.total = 0;
    clone.vendorOrder = null;
    clone.fulfillment = 'PICKUP';
    clone.date = new Date();
    clone.dateAdded = undefined;
    clone.dateUpdated = undefined;
    clone.patient = context.patientId;
    clone.user = context.userId;
    clone.store = context.store;
    delete clone.initials;

    (clone.products || []).forEach((productElement: any) => {
      productElement._id = null;
      productElement.cost = 0;
      productElement.retail = 0;
      productElement.custom = 0;
      productElement.shipping = 0;
      productElement.destination = '';
      productElement.tax = credit ? -Math.abs(credit) : 0;
      const lensPrice = 0;
      if (productElement.lens) {
        productElement.lens.price = 0;
        productElement.lens.labReferenceNumber = '';
        productElement.lens.POLLabReferenceNumber = '';
        productElement.lens.date = '';
        productElement.lens.failedInspection = '';
      }
      productElement.subtotal = productElement.tax + lensPrice;

      if (!productElement.frame) {
        productElement.frame = {};
      }
      productElement.frame.pof = true;
      productElement.frame.deposit = 0;
      productElement.frame.enclosed = false;
      productElement.frame.physicalStatus = '';
      productElement.frame.preOrderStatus = '';
      productElement.frame.activity = [];
      productElement.frame.activityStatus = '';
      productElement.frame.dateReceived = '';
      productElement.frame.followUpDate = '';
      productElement.frame.followUpStatus = '';
      productElement.frame.referenceNumber = '';
      productElement.frame.labVendor = '';
      productElement.frame.carrier = '';
      productElement.frame.trackingNumber = '';
      productElement.frame.shipDate = '';
      productElement.frame.repairItems = [];
      productElement.frame.orderDate = '';
      productElement.frame.failedInspection = false;
    });

    const created = new LogElement();
    created.initials = context.initials;
    created.user = context.userId;
    created.item = 'JOB CREATED';
    created.notes = '';
    created.date = Date.now().toString();

    const rxLog = new LogElement();
    rxLog.initials = context.initials;
    rxLog.user = context.userId;
    rxLog.item = originalInvoice ? `NEW RX FROM ${originalInvoice}` : 'NEW RX';
    rxLog.notes = credit
      ? `PREVIOUS SALES TAX CREDITED/REVERSED ${NewRxJob.formatTaxCredit(credit)} FROM ${originalInvoice}; POF; SELECT ENCLOSED OR CUT & EDGE`
      : 'POF; SELECT ENCLOSED OR CUT & EDGE; ENTER NEW RX AND LENS DETAILS';
    rxLog.date = Date.now().toString();
    clone.log = [created, rxLog];

    return clone as Order;
  }

  static create$(
    original: any,
    originalInvoice: string,
    existingOrders: Order[],
    context: {
      patientId: string;
      userId: string;
      store: any;
      initials: string;
    },
    orderService: OrderService
  ): Observable<{ created: Order; taxCredit: number }> {
    const applyCredit = NewRxJob.shouldApplyFrameOnlyTaxCredit(
      original,
      existingOrders
    );
    const taxCredit = applyCredit ? NewRxJob.parseOrderTax(original) : 0;
    const invoiceNumber = NewRxJob.nextRxInvoice(originalInvoice, existingOrders);
    const newOrder = NewRxJob.buildLinkedNewRxOrder(
      original,
      originalInvoice,
      invoiceNumber,
      taxCredit,
      context
    );
    const originalId = String(original?._id || '').trim();

    return orderService.addOrder(newOrder).pipe(
      switchMap((createdOrders: Order[]) => {
        const created = Array.isArray(createdOrders)
          ? createdOrders[0]
          : createdOrders;
        if (taxCredit <= 0 || !originalId || originalId === 'NEW') {
          return of({ created, taxCredit });
        }
        return orderService
          .updateOrderById(originalId, {
            frameOnlyTaxCreditUsed: true,
          } as Partial<Order>)
          .pipe(
            map(() => ({ created, taxCredit })),
            catchError(() => of({ created, taxCredit }))
          );
      })
    );
  }
}
